import React, { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";

export function DeleteClientButton({ client, onDeleted, size = "sm", variant = "outline" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function confirmDelete() {
    setError(null);
    setLoading(true);
    try {
      await api.delete(`/clients/${client.id}`);
      setOpen(false);
      onDeleted && onDeleted(client);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setError(null); }}>
      <AlertDialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className="text-rose-700 hover:bg-rose-50 hover:text-rose-800"
          data-testid={`delete-client-${client.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Trash2 className="h-4 w-4" /> Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this organization?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block">
              You are about to permanently delete{" "}
              <span className="font-semibold text-slate-900">{client.name}</span>.
            </span>
            <span className="mt-2 block text-rose-600">
              This will remove the organization along with all of their products, crates, shipments, tickets, and organization-user logins. This action cannot be undone.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <p className="rounded-md bg-rose-50 p-3 text-sm text-rose-700">{error}</p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={loading}
            onClick={(e) => { e.preventDefault(); confirmDelete(); }}
            className="bg-rose-600 text-white hover:bg-rose-700"
            data-testid="confirm-delete-client-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Yes, delete organization
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
